import $ from "jquery";
import _ from "lodash";
import * as geojson from "geojson";
import {data} from "azure-maps-control";
import {SELECTION_TYPE} from "../../TerritorySelectionModel";
import {TerritoryOutlinesLayer} from "./TerritoryOutlinesLayer";
import {TerritoryLayerUtils} from "./TerritoryLayerUtils";
import {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import {TerritoryLabelLayer} from "./TerritoryLabelLayer";
import { GeomUtils } from "@esWebAPI/es/util/GeomUtils";
import {BasicText, IEventChannel, InitMapParams, OverlayFeatureClickEvent} from "@esWebAPI/es/model/Models";
import {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import {TerritoryBaseLayer} from "@esWebAPI/es/map/layer/territory/TerritoryBaseLayer";
import {TerritoryPolygonRelatedLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPolygonRelatedLayer";
import { TerritoryPointRelatedLayer } from "./TerritoryPointRelatedLayer";

import type {Geometry, Polygon} from "geojson";
import type {MapMouseEvent} from "azure-maps-control";
import type {AzureBaseMap} from "@esWebAPI/es/map/module/AzureBaseMap";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type {ITerritoryException, ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import type {LassoHandler} from "../../handler/LassoHandler";
import type {EqualCriteria, InCriteria, InteractsCriteria} from "@esWebAPI/es/model/Criteria";
import type {ITerritoryBaseLayer, ITerritoryLayer} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {Feature} from "@esWebAPI/es/model/Feature";

const AssignmentStatus = {
    'NO_BASE_FEATURES_SELECTED': 'NO_BASE_FEATURES_SELECTED',
    'ALREADY_ASSIGNED': 'ALREADY_ASSIGNED',
    'ASSIGNED': 'ASSIGNED', // All or partially, should we split?
    'NONE_ASSIGNED': 'NONE_ASSIGNED',
    'SELECTED': 'SELECTED'
};

export class TerritoryLayerCollection {

    private readonly PLUS_SYMBOL_STYLE = {
        $type: "SymbolStyle",
        color: 4278190080,
        size: 20,
        symbolTypeId: 17,
        text: {
            "bold": false,
            "family": "Arimo",
            "color": 4278190080,
            "height": 16.0,
            "italic": false,
            "position": "BELOW",
            "fontOutlineColor": es.util.color.fromHex("#FFF"),
            mouseOver: false,
            inheritFontColor: false
        } as BasicText
    };

    private readonly MINUS_SYMBOL_STYLE = {
        $type: "SymbolStyle",
        color: 4278190080,
        size: 20,
        symbolTypeId: 55,
        text: this.PLUS_SYMBOL_STYLE.text
    };


    private readonly baseMap: AzureBaseMap;
    private readonly territoryModel: TerritoryModel;
    private readonly featureViewModel: any;
    private readonly overlayModule: AzureOverlay;
    private readonly eventChannel: IEventChannel;

    private _loadComplete: JQuery.Deferred<void> = $.Deferred();
    private internalEventChannel = es.util.createEventChannel({ onTerritoryFeatureMouseOver: 1, onTerritoryFeatureMouseOut: 1 }, "es.map.layer.TerritoryLayerCollection");

    private territoryBaseLayer: TerritoryBaseLayer|undefined;
    private relatedLayers: TerritoryPolygonRelatedLayer[] = [];
    private extraRelatedPointLayer: TerritoryPointRelatedLayer|undefined;
    private outlinesLayer: TerritoryOutlinesLayer|undefined;
    private territoryLabelsLayer: TerritoryLabelLayer|undefined;


    private readonly _isComparisonPage = false;
    private _isTerritoryComparisonMasterMap = false;

    private selectionTimeoutLoadingHandler:  any;

    private lassoFillHandler: LassoHandler|undefined;


    // Cache to store result of whether to display +/- on relatedLayerViews because it may require fetching all
    // baseFeatIds in a us state and comparing with ids in selection model).
    // See #onTerritoryChange SELECT_FEATS for invalidation.
    private selectedPlusMinusCache: {[key:string]: boolean} = {};

    private _selectionEnabled = true;
    private _mouseOverFeat: any|undefined = undefined;

    private _overlayIds: number[] = [];
    private _plusSymbolOverlayId = -1;
    private _highlightedPolygonId = -1;

    private readonly isDoNotRenderTerritoryLayers =  false;

    constructor(baseMapModule: AzureBaseMap, territoryModel: TerritoryModel, featureViewModel: FeatureViewModel,
                overlayModule: AzureOverlay, initMapParams: InitMapParams, eventChannel: any) {

        this.baseMap = baseMapModule;
        this.territoryModel = territoryModel;
        this.featureViewModel = featureViewModel;
        this.overlayModule = overlayModule;
        this.eventChannel = eventChannel;
        this.isDoNotRenderTerritoryLayers = !!initMapParams.isDoNotRenderTerritoryLayers;
        this._isComparisonPage = !!initMapParams.isTerritoryComparison;
        this._isTerritoryComparisonMasterMap = !!initMapParams.isTerritoryComparisonMasterMap;

        if (!territoryModel.hasTerritory()) {
            return;
        }



        this.eventChannel.addListener("onSetSelectionEnabled", (selectionEnabled: boolean) => {
            this._selectionEnabled = selectionEnabled;
            if (!selectionEnabled) {
                this.onTerritoryFeatureMouseOut();
            }
        });

    }

    public async init() {
        const startTime = Date.now();
        console.time("TerritoryLayerCollection : init");

        await this.territoryModel.loadComplete;
        await this.baseMap.loadComplete

        this.baseMap.layers.forEach(layer => {
            if (this.territoryModel.isBaseLayer(layer.layerView.id)) {
                this.territoryBaseLayer = layer as TerritoryBaseLayer;
            }
            if (this.territoryModel.isRelatedExtraPointLayer(layer.layerView.id)) {
                this.extraRelatedPointLayer = layer as TerritoryPointRelatedLayer;
            }
            if (this.territoryModel.isRelatedLayer(layer.layerView.id)) {
                this.relatedLayers.push(layer as TerritoryPolygonRelatedLayer);
            }
        });


        if (this.territoryBaseLayer && this.isDoNotRenderTerritoryLayers){
            es.log.log("isDoNotRenderTerritoryLayers : skip layer", this.territoryBaseLayer);
        } else if (this.territoryBaseLayer) {
            this.outlinesLayer = new TerritoryOutlinesLayer(this.baseMap.map, this.baseMap.getDatasourceManager(),
                this.territoryModel, this.territoryBaseLayer,
                new ThematicStylerFactory(this.territoryModel),
                this.eventChannel);
            this.territoryLabelsLayer = new TerritoryLabelLayer(this.baseMap.map, this.territoryModel, this.outlinesLayer, this.eventChannel);


            this.baseMap.addLayer(this.outlinesLayer);

            await this.territoryModel.getRelatedFeaturesMapping().load(this.territoryBaseLayer, this.relatedLayers);
            await this.outlinesLayer.loadComplete;
        }


        console.timeEnd("TerritoryLayerCollection : init");
        // this.lassoFillHandler = new LassoHandler(baseMapModule.map);
        this.addListeners();

        this.eventChannel.fireEvent("onTerritoryLayerCollectionLoadFinish", es.util.formatMillis(Date.now() - startTime));
        this._loadComplete.resolve();
    }

    get loadComplete(): JQuery.Deferred<void> {
        return this._loadComplete;
    }

    public getTerritoryModel() {
        return this.territoryModel;
    }

    /*
        let showDebugGrid = false;
        let showTerritoryBaseFeatIds = false;
        let showBaseFeatureBalanceValue = false;

        // Small caches (the max size == max num of territories) map used to avoid recalculation of colours
        let colorCache = {};
        let selectedFillStyleCache = {};

        */

    public isFeatSelected(territoryLayer: ITerritoryBaseLayer, featureId: number) {

        const cacheKey = territoryLayer.layerView.id + "_" + featureId;

        // cache results if a query is run. invalidate cache on select/deselect (#onTerritoryChange SELECT_FEATS)
        if (this.selectedPlusMinusCache.hasOwnProperty(cacheKey)) {
            return this.selectedPlusMinusCache[cacheKey];
        }

        const addCacheKey = (selected: boolean) => {
            this.selectedPlusMinusCache[cacheKey] = selected;
            return selected;
        }

        if (territoryLayer instanceof TerritoryOutlinesLayer) {
            return addCacheKey(this.territoryModel.getSelectionModel().isAllUpperSelected(featureId));
        }
        else if (territoryLayer instanceof TerritoryPolygonRelatedLayer) {
            const baseFeatureIds = this.territoryModel.getRelatedFeaturesMapping().get(territoryLayer.layerView.id, featureId);

            if (baseFeatureIds && this.territoryModel.getSelectionModel().getBaseFeatIds().length < baseFeatureIds.length) {
                // if amount of base feats selected is less than is in the parent feature then they couldn't possibly all be selected
                return addCacheKey(false);
            } else {
                return addCacheKey(this.territoryModel.getSelectionModel().isAllBaseSelected(baseFeatureIds));
            }

        }
        else if (territoryLayer instanceof TerritoryBaseLayer) {
            return addCacheKey(this.territoryModel.getSelectionModel().isAllBaseSelected(featureId));
        }
        else if (territoryLayer instanceof TerritoryPointRelatedLayer) {
            return addCacheKey(this.territoryModel.getSelectionModel().isAllBasePointSelected(featureId));
        }
        return false;
    }

    public clearSelectionBusyOverlay() {
        clearTimeout(this.selectionTimeoutLoadingHandler);
        this.selectionTimeoutLoadingHandler = null;
        this.eventChannel.fireEvent("onHideLoadingOverlay");
    }

    public setSelectionBusyOverlay() {
        if (this.selectionTimeoutLoadingHandler) {
            this.clearSelectionBusyOverlay();
        }

        this.selectionTimeoutLoadingHandler = setTimeout(() => {
            this.eventChannel.fireEvent("onShowLoadingOverlay", $i18n("Selecting_areas"));
        }, 2000);
    }

    public isComparisonPage(): boolean {
        return this._isComparisonPage;
    }

    public getLassoFillHandler() {
        return this.lassoFillHandler;
    }

    public getTerritoryLayer(layerViewId: number) {
        const layer = this.baseMap.getLayersByLayerViewId(layerViewId)[0];
        if (layer) {
            return layer as ITerritoryLayer;
        }
        return null;
    }

    public getRelatedLayer(layerViewId: number) {
        if (this.relatedLayers.findIndex(rl => rl.layerView.id === layerViewId) < 0) {
            throw new Error(`Related Layer for id (${layerViewId}) not found.`);
        }
        return this.relatedLayers.filter(rl => rl.layerView.id === layerViewId)[0];
    }

    public getTerritoryBaseLayer() {
        return this.territoryBaseLayer;
    }

    public getTerritoryOutlinesLayer() {
        return this.outlinesLayer;
    }

    public getTerritoryLabelLayer() {
     return this.territoryLabelsLayer;
    }

    public onDiscard() {
        if (!this.territoryModel.isHierarchical()) {
            return es.util.deferred.resolved();
        }
        return this.outlinesLayer?.clear();
    }

    private removeAllOverlays() {
        if (this._overlayIds.length > 0) {
            this.overlayModule.removeAll(this._overlayIds);
            this._overlayIds = [];
        }
    }

    /**
     * Perform assignment of territory polygon and points if applicable, given array of base polygon feats.
     *
     * @param baseFeats base polygon feats
     * @param territoryId territory to assign to. Current selected territory if omitted.
     *
     * @return Deferred that will be resolved with an AssignmentStatus
     */
    private performAssignmentOfBaseFeatsAndPoints(baseFeats: any[], territoryId: number) {
        if (!baseFeats.length) {
            return es.util.deferred.resolved({ status: AssignmentStatus.NO_BASE_FEATURES_SELECTED });
        }

        // if the ids are string convert them to number (this happens if the feats are fetched from terraformer)
        if (typeof (baseFeats[0].id) == "string") {
            baseFeats.forEach(function(bf) { bf.id = parseInt(bf.id); });
        }

        const initialBaseFeatureIds = this.territoryModel.getBaseFeatureIds(territoryId).slice();
        const initialBasePointFeatureIds = this.territoryModel.getBasePointFeatureIds(territoryId).slice();

        const originalAssignmentMap = this.getAssignmentMap(baseFeats);

        const assignedBaseFeats = this.assignBaseFeatures(baseFeats, territoryId);

        // keep only base features that got assigned
        const assignedBaseFeatIds = assignedBaseFeats.map(f => f.id);
        Object.keys(originalAssignmentMap).forEach((tid: string) => {
            originalAssignmentMap[+tid] = originalAssignmentMap[+tid].filter(function(id: number) { return assignedBaseFeatIds.includes(id); });
            if (originalAssignmentMap[+tid].length === 0) {
                delete originalAssignmentMap[+tid];
            }
        });

        return this.assignPointsInsideBaseFeatures(assignedBaseFeats, territoryId, originalAssignmentMap).then(function(pointFeatIds: number[]) {

            const newBaseFeatIds = assignedBaseFeatIds.filter(function(id) { return !initialBaseFeatureIds.includes(id); });
            const newPointFeatIds = pointFeatIds.filter(function(id) { return !initialBasePointFeatureIds.includes(id); });

            if (assignedBaseFeatIds.length === baseFeats.length) { // all base features were assigned
                if (!newBaseFeatIds.length && !newPointFeatIds.length) { // no new feature, all belonged to territory already (or had cuckoo points and decided not to steal!)
                    return { status: AssignmentStatus.ALREADY_ASSIGNED }; // TODO FIXME Is this ok?? Even if decided not to steal cuckoo points?!
                }

                return { status: AssignmentStatus.ASSIGNED, baseFeatureIds: newBaseFeatIds, basePointIds: newPointFeatIds };
            }

            // else... some base features were not assigned (could not steal)
            // FIXME :: Can this be removed??
            if (!newBaseFeatIds.length && !newPointFeatIds.length) {
                return { status: AssignmentStatus.NONE_ASSIGNED };
            }

            return { status: AssignmentStatus.ASSIGNED, baseFeatureIds: newBaseFeatIds, basePointIds: newPointFeatIds };
        });
    }

    private performAssignmentOfUpperLevels(territoryIds: number[], targetTerritoryId: number, isSuppressEvents: boolean = false) {

        console.log("performAssignmentOfUpperLevels", territoryIds, "to", targetTerritoryId);

        if (territoryIds.length === 0) {
            throw new Error("performAssignmentOfUpperLevels : no territoryIds specified");
        }
        if (!targetTerritoryId) {
            throw new Error("performAssignmentOfUpperLevels no territoryId specified");
        }

        let assignedTer = [];
        const affectedParentsIds = new Set();
        affectedParentsIds.add(targetTerritoryId);
        for (let i = 0; i < territoryIds.length; i++) {
            const territory = this.territoryModel.get(territoryIds[i]);
            affectedParentsIds.add(territory.ES_PARENT_ID);
            territory.ES_PARENT_ID = targetTerritoryId;
            assignedTer.push(territory);

            const children = this.territoryModel.getByParentId(territoryIds[i], false);
            for (let x = 0; x < children.length; x++) {
                if (territoryIds.includes(children[x].ES_ID)) {
                    children[x].ES_PARENT_ID = targetTerritoryId;
                    assignedTer.push(children[x]);
                }
            }
        }
        if (!isSuppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", { "MOVE": true }, assignedTer, [...affectedParentsIds]);
        }
    }

    /**
         * Select base feats (and points if applicable) located in any of the specified base features
         * See also: #performAssignmentOfBaseFeatsAndPoints
         */
    public async performSelectionOfBaseFeatsAndPoints(baseFeatIds: number[]) {
        if (this.territoryModel.hasRelatedExtraPointLayer()) {
            const pointFeats = await this.getPointFeaturesForBaseFeatureIds(baseFeatIds);
            const pointFeatIds = pointFeats.map((f) => f.id as number);
            this.territoryModel.getSelectionModel().addBasePoints(pointFeatIds, true);
        }

        let pointLayerViews = TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel);
        this.territoryModel.getSelectionModel().addBaseFeats(baseFeatIds, pointLayerViews.length > 0);
        this.selectImplicitPoints(baseFeatIds);
    }

    /**
     * Deselect base feats (and points if applicable) located in any of the specified base features
     * See also: performUnAssignmentOfBaseFeatsAndPoints
     */
    public async performDeSelectionOfBaseFeatsAndPoints(baseFeatIds: number[]) {
        if (this.territoryModel.hasRelatedExtraPointLayer()) {
            const pointFeats = await this.getPointFeaturesForBaseFeatureIds(baseFeatIds);
            const pointFeatIds = pointFeats.map(f => f.id as number);
            this.territoryModel.getSelectionModel().removeBasePoints(pointFeatIds, true);
        }
        let pointLayerViews = TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel);
        this.territoryModel.getSelectionModel().removeBaseFeats(baseFeatIds, pointLayerViews.length > 0);
        this.unselectImplicitPoints(baseFeatIds);
    }

    /**
     * Select upper level territories.
     * See also: performAssignmentOfUpperLevels
     */
    public performSelectionOfUpperLevels(territoryIds: number[]) {
        if (this.territoryModel.isAccountOnly()) {
            const upperLevelTers = territoryIds.map(terId => { return { territoryId: terId, exceptions: this.territoryModel.getExceptions(terId) } });
            this.territoryModel.getSelectionModel().addUpperLevels(upperLevelTers, false);
            return;
        }

        const upperLevelTers = [];
        const allBaseFeatIds = [];
        for (const terId of territoryIds) {
            const upperLevelTer = {
                territoryId: terId,
                baseFeatIds: this.territoryModel.getBaseFeatureIds(terId),
                basePointIds: this.territoryModel.getBasePointFeatureIds(terId),
                exceptions: this.territoryModel.getExceptions(terId)
            };
            upperLevelTers.push(upperLevelTer);
            allBaseFeatIds.push(...upperLevelTer.baseFeatIds);
        }

        // let pointLayerViews = TerritoryLayerUtils.getDatasetLayerViews(territoryModel, featureViewModel);
        this.territoryModel.getSelectionModel().addUpperLevels(upperLevelTers);
        this.selectImplicitPoints(allBaseFeatIds);
    }

    /**
     * DeSelect upper level territories.
     */
    public performDeSelectionOfUpperLevels(territoryIds: number[]) {
        this.territoryModel.getSelectionModel().removeUpperLevels(territoryIds);
    }

    /**
     * For the specified base features, returns a Map of currently assigned territoryId -> base features ids
     */
    private getAssignmentMap(baseFeats: any[]) {
        let map: {[territoryId: number]: number[]} = {};
        baseFeats.forEach((f) => {
            let territoryId = this.territoryModel.getByBaseFeatureId(f.id)?.ES_ID;
            if (territoryId) {
                if (!map[territoryId]) {
                    map[territoryId] = [];
                }
                map[territoryId].push(f.id);
            }
        });
        return map;
    }

    public assignBaseFeatures(baseFeats: any[], territoryId: number) {
        this.territoryModel.assignBaseFeatures(baseFeats, territoryId, true);
        return baseFeats;
    }

    /**
     * Given a layerView and featureId of a polygon layer (can be baselayer, related layer or non-related layer),
     * detect which territories have feats within the feature.
     * e.g. given a layerViewId for us states and a feature id for texas, tell me which territories have features in texas
     * @return array of territoryFeatures
     */
    public getTerritoriesAssociatedByLayerViewAndFeatureId(layerViewId: number, featureId: number, level?: number) {

        const getTerritoriesWithBaseFeatIds = (featureIds: number[], pointFeatureIds: number[]): ITerritoryFeature[] => {
            const rs: ITerritoryFeature[] = [];

            const findOne = function(haystack: any, arr: any) {
                return arr.some(function(v: any) {
                    return haystack.includes(v); // TODO: use binaryserach?
                });
            };

            const territoryFeatures = this.territoryModel.getTerritoryFeaturesObjRef();
            for (let i = 0; i < territoryFeatures.length; i++) {
                if ((findOne(territoryFeatures[i].ES_BASE_FEATURE_IDS, featureIds)) ||
                    (territoryFeatures[i].ES_BASE_POINT_IDS && findOne(territoryFeatures[i].ES_BASE_POINT_IDS, pointFeatureIds))) {

                    if (this.territoryModel.isHierarchical() && level && level !== territoryFeatures[i].ES_LEVEL) {
                        continue;
                    }
                    rs.push(territoryFeatures[i]);
                }
            }

            return rs;
        }

        return new Promise<ITerritoryFeature[]>((resolve, reject) => {
            if (this.territoryModel.isBaseLayer(layerViewId)) {
                // query is directly on base layerview
                if (this.extraRelatedPointLayer) {
                    this.extraRelatedPointLayer.featureWorker.getFeaturesByProperty(this.territoryModel.getExtraPointLinkAttributeName(), featureId).then((pointFeats: geojson.Feature[]) => {
                        resolve(getTerritoriesWithBaseFeatIds([featureId], pointFeats.map(f => +f.id)));
                    });
                } else {
                    resolve(getTerritoriesWithBaseFeatIds([featureId], []));
                }
            } else if (this.territoryModel.isRelatedLayer(layerViewId)) {
                // query is via a relatedLayerView (e.g. user is assigning zcta by us state)
                const selectCriteria = this.getTerritoryLayer(layerViewId)?.layerView.selectCriteria as EqualCriteria<any>;
                this.getRelatedLayer(layerViewId).featureWorker.getFeatureById(featureId).then((fullFeat: geojson.Feature)=> {
                    const selectCriteriaAttrVal = fullFeat.properties[selectCriteria.attr]; // e.g. STATE_FIPS, 12

                    const baseFeaturesDfd = $.Deferred();
                    this.territoryBaseLayer?.featureWorker.getFeaturesByProperty(selectCriteria.attr, selectCriteriaAttrVal).then((feats: geojson.Feature[])=> {
                        const baseFeatureIds = feats.map(f => f.id);

                        if (this.extraRelatedPointLayer) {
                            this.extraRelatedPointLayer.featureWorker.getFeaturesByProperty(this.territoryModel.getExtraPointLinkAttributeName(), baseFeatureIds).then((pointFeats: geojson.Feature[]) => {
                                baseFeaturesDfd.resolve(baseFeatureIds, pointFeats.map((f: any) => f.id));
                            });
                        } else {
                            baseFeaturesDfd.resolve(baseFeatureIds, []);
                        }
                    });

                    baseFeaturesDfd.then((baseFeatureIds: number[], pointFeatureIds: number[]) => {
                        resolve(getTerritoriesWithBaseFeatIds(baseFeatureIds, pointFeatureIds));
                    });
                });

            } else if (!this.territoryModel.getRelatedLayerViewMapping()) {
                // there's no relatedLayerView (probably custom territory)

                // fetch the feature and get the geom
                es.api.feature.get(layerViewId, featureId).done((feat: any)=> {
                    const geoJsonGeom =  GeomUtils.serverToGeoJson(feat.geom); //TODO Get Feat from client
                    const baseFeatureDfd = $.Deferred();
                    this.territoryBaseLayer?.featureWorker.intersects(geoJsonGeom).then((feats: geojson.Feature[]) => {
                        const baseFeats: any[] = [];
                        for (let f = 0; f < feats.length; f++) {
                            if (this.territoryBaseLayer && TerritoryLayerUtils.isSelectable(this.territoryBaseLayer, feats[f])) {
                                baseFeats.push(feats[f]);
                            }
                        }

                        if (this.extraRelatedPointLayer) {
                            this.extraRelatedPointLayer.featureWorker.intersects(geoJsonGeom).then((rs) => {
                                const basePointFeats = [];
                                for (let f = 0; f < rs.length; f++) {
                                    if (this.territoryBaseLayer && TerritoryLayerUtils.isSelectable(this.territoryBaseLayer, rs[f])) {
                                        basePointFeats.push(rs[f]);
                                    }
                                }
                                baseFeatureDfd.resolve(baseFeats.map(f => f.id), basePointFeats.map(f => f.id));
                            });
                        } else {
                            baseFeatureDfd.resolve(baseFeats.map(f => f.id), []);
                        }
                    });

                    baseFeatureDfd.then((baseFeatureIds: number[], pointFeatureIds: number[]) => {
                        resolve(getTerritoriesWithBaseFeatIds(baseFeatureIds, pointFeatureIds));
                    });
                });

            } else {
                reject("getTerritoriesAssociatedByLayerViewAndFeatureId called with invalid layerViewId " + layerViewId);
            }
        });
    }

    /**
     * Returns the layerViewId of the currently visible _related_ layerViewId (e.g. in a zcta-based territory, related
     * layers would be county and states). Returns -1 if no related layer or no related layers are currently visible.
     */
    private getVisibleBaseOrRelatedLayerViewIdWithLowestMaxRes() {
        let layerViewIdWithLowestMaxRes = null;
        if (this.territoryModel.getRelatedLayerViewMapping()) {
            const relatedLayerView = this.territoryModel.getRelatedLayerViewMapping()?.relatedLayerView;
            if(!relatedLayerView){
                if(!this.territoryModel.isAccountOnly()){
                    console.warn("Could not fetch getRelatedLayerViewMapping and map is NOT AO", this.territoryModel.getRelatedLayerViewMapping());
                }
                return -1;
            }
            const layerViewIds = Object.keys(relatedLayerView);
            layerViewIds.unshift(this.territoryModel.getManageTerritoryBaseLayerView().getId());
            
            for (let i = 0; i < layerViewIds.length; i++) {
                const tl = this.getTerritoryLayer(Number(layerViewIds[i])); // TODO: convert keys (layerViewIds) from string to int

                const layerWithLowestMaxRes = layerViewIdWithLowestMaxRes ? this.getTerritoryLayer(layerViewIdWithLowestMaxRes) : null;

                if ((tl && tl.isVisibleRes()) &&
                    (!layerWithLowestMaxRes || tl.layerView.maxResolution < layerWithLowestMaxRes.layerView.maxResolution)) {

                    layerViewIdWithLowestMaxRes = tl.layerView.id;
                }
            }
        }
        return layerViewIdWithLowestMaxRes;
    }

    /**
     * Related layers may be selectable at some or all zoom levels (e.g. Us States) but we do not
     * want them to be _assignable_. e.g. we do not think that a user would ever want to assign by state when they
     * are zoomed in at county or zcta level, even if they are viewing the state info popup.
     *
     * @param layerViewId
     * @returns {boolean}
     */
    public isLayerAssignableAtCurrentZoom(layerViewId: number) {
        return this.getVisibleBaseOrRelatedLayerViewIdWithLowestMaxRes() === layerViewId;
    }

    /**
     * Assign contents of selectionModel to territoryModel
     *
     * @param territoryId
     * @param isSuppressEvents
     * @returns {*}
     */
    public applySelection(territoryId: number, isSuppressEvents:boolean = false) {
        const dfd = $.Deferred();
        const selModel = this.territoryModel.getSelectionModel();
        const curSelMode = selModel.getMode();

        if (curSelMode === "BASE") {
            const baseFeatIds = selModel.getBaseFeatIds();
            const basePointsIds = selModel.getBasePointIds();
            let [exceptionsToAdd, exceptionsToRemove] = selModel.computeAndGetExceptions(territoryId);
            const exToAdd: {[territoryId: string]: ITerritoryException[]} = {}

            if (territoryId === -1) {
                exceptionsToRemove = exceptionsToAdd.flatMap((eta: any) => eta.exceptions).concat(exceptionsToRemove);
                this.territoryModel.unAssignExceptions(exceptionsToRemove as ITerritoryException[], true);
                // If it isAccountOnly. We do not add special territory for unassigned. It is not necessary. It will just remain unassigned on the pointMapping
                if (!this.territoryModel.isAccountOnly()) {
                    for (const exception of exceptionsToAdd) {
                        const val = exception as  {territoryId: string, exceptions: ITerritoryException[]}
                        exToAdd[+val.territoryId] = val.exceptions;
                    }
                    this.territoryModel.assignExceptionsInBulk(exToAdd);
                }
            } else {

                for (const exception of exceptionsToAdd) {
                    const val = exception as  {territoryId: string, exceptions: ITerritoryException[]}
                    exToAdd[+val.territoryId] = val.exceptions;
                }
                this.territoryModel.assignExceptionsInBulk(exToAdd);
                this.territoryModel.unAssignExceptions(exceptionsToRemove as ITerritoryException[], true);
            }


            const eventData: any = {};
            if (!_.isEmpty(exceptionsToRemove)) {
                eventData["REMOVE_EXCEPTIONS"] = exceptionsToRemove;
            }

            if (!_.isEmpty(exToAdd)) {
                eventData["ADD_EXCEPTIONS"] = exToAdd;
            }

            if (this.territoryModel.isAccountOnly()) {
                if (!_.isEmpty(eventData)) {
                    // FIXME :: onTerritoryChange Shouldn't be fired from this file
                    this.eventChannel.fireEvent("onTerritoryChange", eventData, [this.territoryModel.get(territoryId)], this.territoryModel.getChangedTerritoriesIds());
                }
                selModel.reset();
                return es.util.deferred.resolved();
            }

            if (territoryId === -1) {
                if (baseFeatIds.length > 0 || basePointsIds.length > 0) {
                    const baseFeaturesMockObj = baseFeatIds.map(id => { return { id } });
                    const pointFeaturesMockObj = basePointsIds.map(id => { return { id } });
                    this.territoryModel.unAssignPolygonAndPointFeatures(baseFeaturesMockObj, pointFeaturesMockObj, true);
                    eventData["REMOVE_FEATS"] = { baseFeatureIds: baseFeatIds, basePointIds: basePointsIds }
                }

                if (!_.isEmpty(eventData)) {
                    // FIXME :: onTerritoryChange Shouldn't be fired from this file
                    const lowerLevelChangedTerritories = this.territoryModel.getTerritoryFeaturesObjRef().filter(
                        t => this.territoryModel.getChangedTerritoriesIds().includes(t.ES_ID) && (t.ES_LEVEL === undefined || t.ES_LEVEL === this.territoryModel.getTerritoryMetaObjRef().levels)
                    );
                    this.eventChannel.fireEvent("onTerritoryChange", eventData, lowerLevelChangedTerritories, this.territoryModel.getChangedTerritoriesIds());
                }

                selModel.reset();
                return es.util.deferred.resolved();
            }

            const basePtsDf = $.Deferred();
            if (basePointsIds.length > 0) {
                eventData["ADD_FEATS"] = {}
                this.extraRelatedPointLayer?.featureWorker.getFeaturesByIds(basePointsIds).then((basePointFeatures: geojson.Feature[])=> {
                    this.territoryModel.assignPointFeatures(basePointFeatures, territoryId, true);
                    eventData["ADD_FEATS"].basePointIds = basePointsIds;
                    basePtsDf.resolve();
                });
            } else {
                basePtsDf.resolve();
            }

            const baseFeatsDfd = $.Deferred();
            if (baseFeatIds.length > 0) {
                this.territoryBaseLayer?.featureWorker.getFeaturesByIds(baseFeatIds).then((baseFeats: geojson.Feature[])=> {
                    // TODO: performAssignmentOfBaseFeatsAndPoints does points as well feats. if selection tool handles these separately will need to create new method

                    this.performAssignmentOfBaseFeatsAndPoints(baseFeats, territoryId).then(({ status, baseFeatureIds} : any) => {
                        if (status === AssignmentStatus.ASSIGNED) {
                            if (!eventData["ADD_FEATS"]) {
                                eventData["ADD_FEATS"] = {}
                            }
                            eventData["ADD_FEATS"].baseFeatureIds = baseFeatureIds;
                            baseFeatsDfd.resolve();
                        } else if (status === AssignmentStatus.ALREADY_ASSIGNED) {
                            baseFeatsDfd.resolve();
                        }
                    });
                });
            } else {
                baseFeatsDfd.resolve();
            }

            Promise.all([basePtsDf, baseFeatsDfd]).then(() => {
                if (!_.isEmpty(eventData)) {
                    // FIXME :: onTerritoryChange Shouldn't be fired from this file
                    this.eventChannel.fireEvent("onTerritoryChange", eventData, [this.territoryModel.get(territoryId)], this.territoryModel.getChangedTerritoriesIds());
                }
                selModel.reset();
                dfd.resolve(true);
            });
        } else if (curSelMode === "UPPER") {
            const upperLevels = this.territoryModel.getSelectionModel().getUpperLevels();
            this.performAssignmentOfUpperLevels(upperLevels, territoryId, isSuppressEvents);
            selModel.reset();
            return es.util.deferred.resolved();
        } else {
            console.error("applySelection: there is nothing selected");
            return es.util.deferred.rejected();
        }

        return dfd.promise();
    }

    /**
     * See also: #assignByLayerViewAndFeatureId
     * @param layerViewId
     * @param featureId
     * @param action
     * TODO: rename this method: featureId can be a territoryId at upper levels
     */
    public selectByLayerViewAndFeatureId(layerViewId: number, featureId: number, action?: any) {

        this.setSelectionBusyOverlay();

        if (!this.territoryModel.isHierarchical() || this.territoryModel.isLowestLevel()) {

            const updateBaseFeatureSelections = async (baseFeats: any[]) => {
                if (!baseFeats.length) {
                    return;
                }

                // if the ids are string convert them to number (this happens if the feats are fetched from terraformer)
                if (typeof (baseFeats[0].id) == "string") {
                    baseFeats.forEach(function(bf) {
                        bf.id = parseInt(bf.id);
                    });
                }

                const baseFeatIds = baseFeats.map(f => f.id);

                if (action === "REMOVE" || this.territoryModel.getSelectionModel().isAllBaseSelected(baseFeatIds)) {
                    this.performDeSelectionOfBaseFeatsAndPoints(baseFeatIds);
                } else {
                    this.performSelectionOfBaseFeatsAndPoints(baseFeatIds);
                }
                this.clearSelectionBusyOverlay();
            }

            if (this.territoryModel.isBaseLayer(layerViewId) && this.territoryBaseLayer) {

                if (this.territoryModel.getSelectionModel().getSelectionType() === SELECTION_TYPE.ACCOUNTS_ONLY) {
                    this.updateDatasetPointsByBaseFeatureIds([featureId], action);
                } else {
                    updateBaseFeatureSelections([{ id: featureId }]);
                }
                this.updatePlusMinusSymbol(this.territoryBaseLayer, featureId);

            }
            else if (this.territoryModel.isRelatedExtraPointLayer(layerViewId)) {

                // assignment of a single related extra point
                if (action === "ADD") {
                    this.territoryModel.getSelectionModel().addBasePoints(featureId, false);
                } else if (action === "REMOVE") {
                    this.territoryModel.getSelectionModel().removeBasePoints(featureId, false);
                } else {
                    throw new Error("assignByLayerViewAndFeatureId called for assignment of a single RelatedPointLayer feature  with no action param (ADD/REMOVE)");
                }

            }
            else if (this.territoryModel.isRelatedLayer(layerViewId)) {
                // assignment is via a relatedLayerView (e.g. user is assigning zcta by us state)

                const baseFeatureIds = this.territoryModel.getRelatedFeaturesMapping().get(layerViewId, featureId)

                this.territoryBaseLayer?.featureWorker.getFeaturesByIds(baseFeatureIds).then((feats: geojson.Feature[]) => {

                    if (this.territoryModel.getSelectionModel().getSelectionType() === SELECTION_TYPE.ACCOUNTS_ONLY) {
                        this.updateDatasetPointsByBaseFeatureIds(feats.map((f: any) => f.id), action);
                    } else {
                        if (this.territoryModel.getFilter()) {
                            feats = feats.filter((baseFeat: any) => {
                                return this.territoryBaseLayer && TerritoryLayerUtils.isSelectable(this.territoryBaseLayer, baseFeat);
                            });
                        }
                        updateBaseFeatureSelections(feats);
                        this.updatePlusMinusSymbol(this.getTerritoryLayer(layerViewId) as ITerritoryBaseLayer, featureId);
                    }
                });
            }
            else if (!this.territoryModel.getRelatedLayerViewMapping()) {
                // there's no relatedLayerView (probably custom territory)
                updateBaseFeatureSelections([{ id: featureId }]);
            }
        }
        else {
            // is upperlevel click
            const selectedTerId = featureId;

            if (action === "REMOVE" || this.territoryModel.getSelectionModel().isAllUpperSelected(selectedTerId)) {
                this.performDeSelectionOfUpperLevels([selectedTerId]);
            } else {
                this.performSelectionOfUpperLevels([selectedTerId]);
            }
            if (this.outlinesLayer) {
                this.updatePlusMinusSymbol(this.outlinesLayer, featureId);
            }
            this.clearSelectionBusyOverlay();
        }
    }

    public async getPointFeaturesForBaseFeatureIds(baseFeaturesIds: number[]): Promise<geojson.Feature[]> {

        if (!this.extraRelatedPointLayer || !this.territoryModel.hasRelatedExtraPointLayer() || baseFeaturesIds.length === 0) {
            return [];
        }

        const linkAttr = this.territoryModel.getExtraPointLinkAttributeName();
        return this.extraRelatedPointLayer.featureWorker.getFeaturesByProperty(linkAttr, baseFeaturesIds);
    }

    /**
     * Assign zip points located in any of the specified features
     */
    public assignPointsInsideBaseFeatures(features: any[], territoryId: number, originalAssignmentMap: any) {
        if (!this.territoryModel.hasRelatedExtraPointLayer()) {
            return es.util.deferred.resolved([]);
        }

        return this.getSplitPointAssignment(features.map(f => f.id), originalAssignmentMap).then((splitPointFeatures: any) => {
            const toAssign = splitPointFeatures.safe.slice();
            if (splitPointFeatures.cuckoo.length) {
                Array.prototype.push.apply(toAssign, splitPointFeatures.cuckoo);
            }

            if (toAssign.length) {
                this.territoryModel.assignPointFeatures(toAssign, territoryId);
            }

            return toAssign.map(function(pf: any) { return pf.id; });
        });
    }

    /**
     * Gets point assignment split between safe and cuckoo points
     */
    private getSplitPointAssignment(baseFeatureIds: number[], originalAssignmentMap: any) {
        let unassignedBaseFeatureIds = baseFeatureIds.slice();
        for (let tid in originalAssignmentMap) {
            const territoryAssignBaseFeatureIds = originalAssignmentMap[tid];
            unassignedBaseFeatureIds = unassignedBaseFeatureIds.filter(function(id) { return !territoryAssignBaseFeatureIds.includes(id); });
        }

        const territoryId = this.territoryModel.getCurrentTerritoryObjRef()?.ES_ID;

        const getSplitAssignment = (baseFeatureIds: number[]) => {
            return this.getPointFeaturesForBaseFeatureIds(baseFeatureIds).then((pointFeats: any[]) => {
                if (!pointFeats) {
                    pointFeats = [];
                }

                const splitPointFeatures = pointFeats.filter((pf: any) => {
                    return this.extraRelatedPointLayer && TerritoryLayerUtils.isSelectable(this.extraRelatedPointLayer, pf);
                }).reduce((a, pf) => {
                    const t = this.territoryModel.getByBasePointFeatureId(pf.id);

                    let isCuckoo = false;
                    if (t && t.ES_ID != territoryId) {
                        var pfLinkId = pf.properties[this.territoryModel.getExtraPointLinkAttributeName()];
                        var originalTerritoryId = this.getOriginalTerritoryId(pfLinkId, originalAssignmentMap);

                        isCuckoo = (t.ES_ID != originalTerritoryId);
                    }

                    a[isCuckoo ? 'cuckoo' : 'safe'].push(pf);
                    return a;
                }, { 'safe': [], 'cuckoo': [] });

                return splitPointFeatures;
            });
        };

        const dfds = [];
        for (let tid in originalAssignmentMap) {
            dfds.push(getSplitAssignment(originalAssignmentMap[tid]));
        }
        if (unassignedBaseFeatureIds.length) {
            dfds.push(getSplitAssignment(unassignedBaseFeatureIds));
        }

        return Promise.all(dfds).then((args) => {
            var splitPointFeatures = args.reduce(function(a, splitPointFeatures) {
                Array.prototype.push.apply(a.safe, splitPointFeatures.safe);
                Array.prototype.push.apply(a.cuckoo, splitPointFeatures.cuckoo);

                return a;
            }, { 'safe': [], 'cuckoo': [] });

            return splitPointFeatures;
        });
    }

    private getOriginalTerritoryId = function(baseFeatureId: number, originalAssignmentMap: any) {
        for (let tid in originalAssignmentMap) {
            if (originalAssignmentMap[tid].includes(baseFeatureId)) {
                return parseInt(tid);
            }
        }

        return null;
    };

    private selectByGeom(geom: Polygon) {
        const dfd = $.Deferred();

        if (this.territoryModel.isLowestLevel()) {
            this.getBaseFeaturesByGeom(geom).then(features => {

                const featureIds = features.filter(feat => {
                    return this.territoryBaseLayer && TerritoryLayerUtils.isSelectable(this.territoryBaseLayer, feat);
                }).map(feat => feat.id);
                this.performSelectionOfBaseFeatsAndPoints(featureIds);
                if (featureIds.length === 0) {
                    this.clearSelectionBusyOverlay();
                }

                dfd.resolve(AssignmentStatus.SELECTED);
            });
        }
        else {
            this.outlinesLayer?.featureWorker.anyInteract(geom, (features: geojson.Feature[]) => {
                const level = this.territoryModel.getCurrentLevel();
                let ids = features.filter(f => level && f.properties.level === level + 1).map(f => f.properties.territoryId);

                const filter = this.territoryModel.getFilter();
                if (filter !== null) {
                    ids = _.intersection(ids, this.territoryModel.getFilter())
                }

                if (ids.length === 0) {
                    this.clearSelectionBusyOverlay();
                } else {
                    this.performSelectionOfUpperLevels(ids);
                }
                dfd.resolve(AssignmentStatus.SELECTED);
            });
        }
        return dfd.promise();
    }

    private updateDatasetPointsByBaseFeatureIds(baseFeatureIds: number[], action: any) {

        const datasetPointIdsDfds: any[] = []
        TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel).forEach(layerView => {
            datasetPointIdsDfds.push(
                this.getDatasetPointsByLinkAttrs(this.territoryModel.getLinkAttributeName(), baseFeatureIds, layerView.id));
        });

        Promise.all(datasetPointIdsDfds).then((featuresPerLayerViews) => {
            if (action === "REMOVE") {
                this.unselectDatasetPoints(featuresPerLayerViews);
            } else {
                this.selectDatasetPoints(featuresPerLayerViews)
            }
        });
    }

    public async selectDatasetPointsByGeom(geom: Polygon) {
        const dfd = $.Deferred();

        const featuresPerLayerViews = await this.getAllDatasetPointIdsByGeom(geom);

        if (!this.territoryModel.isLowestLevel()) {

            let filter = this.territoryModel.getFilter();

            let territoryIds = featuresPerLayerViews.flatMap(({ layerViewId, features }) => {
                const layerView =  this.featureViewModel.getLayerById(layerViewId);
                const layerMetaId = layerView.getLayerMetaId();
                return features.map((feature: any) => {
                    const territoryId = this.territoryModel.getPointMapping().getNode(layerMetaId, feature.id)?.territoryId as number;
                    let ancestors = this.territoryModel.getAncestors(territoryId, "ES_ID", this.territoryModel.getCurrentLevel() as number + 1);
                    // there will be only 1 ancestor
                    return ancestors[0];

                });
            }).filter(territoryId => territoryId > 0 && (!filter || filter.includes(territoryId)));

            territoryIds = _.uniq(territoryIds);
            this.performSelectionOfUpperLevels(_.uniq(territoryIds));

            if (territoryIds.length === 0) {
                this.clearSelectionBusyOverlay();
            }

        } else {
            this.selectDatasetPoints(featuresPerLayerViews)
        }

        dfd.resolve(AssignmentStatus.SELECTED);


        return dfd.promise();
    }

    public selectDatasetPoints(featuresPerLayerViews: any) {
        // skip layers that don't have points in the selected area
        featuresPerLayerViews = featuresPerLayerViews.filter((fplv: any) => fplv.features.length > 0);
        const featureStateArrayPerLayerMetaId: any = {};
        let isPointInSelection = false;
        for (const featuresPerLayerView of featuresPerLayerViews) {

            const { layerViewId, features } = featuresPerLayerView;
            const layerView = this.featureViewModel.getLayerById(layerViewId);
            const layerMetaId = layerView.getLayerMetaId();
            const featureStateArray = [];


            const filter = this.territoryModel.getFilter();
            if (this.territoryModel.isAccountOnly()) {

                for (let i = 0; i < features.length; i++) {
                    const territoryId = this.territoryModel.getByExceptionFeatureId(layerMetaId, features[i].id)?.ES_ID ?? -1;
                    const isLocked = this.territoryModel.getPointMapping().getNode(layerMetaId, features[i].id)?.isLocked || false;
                    if (territoryId === -1 || filter === null || filter?.includes(territoryId)) {
                        featureStateArray.push({
                            featureId: features[i].id,
                            state: {
                                territoryId: territoryId,
                                isSelected: !isLocked
                            }
                        });
                    }
                }
            } else {
                for (let i = 0; i < features.length; i++) {

                    const node = this.territoryModel.getPointMapping().getNode(layerMetaId, features[i].id);
                    if(!node) {
                        console.warn(`Trying to select a point that doesn't exist: {layerMetaId: ${layerMetaId}}, {featureId: ${features[i].id}} f`, features[i]);
                        continue;
                    }

                    const isException = node.isException;
                    const territoryId =node.territoryId;

                    if (territoryId === -1 || filter === null || filter?.includes(territoryId)) {
                        featureStateArray.push({
                            featureId: features[i].id,
                            state: {
                                territoryId: territoryId,
                                isSelected: !isException
                            }
                        });
                    }
                }
            }

            // Only trigger onSelectionChange once
            isPointInSelection = isPointInSelection || featureStateArray.length > 0;
            if (featureStateArray.length > 0) {
                featureStateArrayPerLayerMetaId[layerMetaId] = featureStateArray;
            }
        }

        const layerMetaIdsInSelectionCount = Object.keys(featureStateArrayPerLayerMetaId).length
        for (const [index, layerMetaId] of Object.keys(featureStateArrayPerLayerMetaId).entries()) {
            this.territoryModel.getSelectionModel().toggleDatasetPointsSelection(+layerMetaId, featureStateArrayPerLayerMetaId[layerMetaId], index !== layerMetaIdsInSelectionCount - 1);
        }

        if (!isPointInSelection) {
            this.clearSelectionBusyOverlay();
        }

    };

    public unselectDatasetPoints(featuresPerLayerViews: any) {
        // skip layers that don't have points in the selected area
        featuresPerLayerViews = featuresPerLayerViews.filter((pplv: any) => pplv.features.length > 0);

        for (const [index, featuresPerLayerView] of featuresPerLayerViews.entries()) {
            const { layerViewId, features } = featuresPerLayerView;
            const layerView = this.featureViewModel.getLayerById(layerViewId);
            const layerMetaId = layerView.getLayerMetaId();
            // Only trigger onSelectionChange once
            this.territoryModel.getSelectionModel().unselectDatasetPoints(layerMetaId, features.map((f: any) => f.id), index !== featuresPerLayerViews.length - 1);
        }
    }

    public selectImplicitPoints(baseFeatIds: number[]) {

        baseFeatIds = Array.isArray(baseFeatIds) ? baseFeatIds : [baseFeatIds];

        if (baseFeatIds.length === 0) {
            return;
        }

        const pointLayerViews = TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel);

        const datasetPointIdsDfds: JQuery.Promise<any>[] = []
        pointLayerViews.forEach(layerView => {
            datasetPointIdsDfds.push(
                this.getDatasetPointsByLinkAttrs(this.territoryModel.getLinkAttributeName(), baseFeatIds, layerView.id));
        });

        Promise.all(datasetPointIdsDfds).then((featuresPerLayerViews) => {
            for (const [index, featuresPerLayerView] of featuresPerLayerViews.entries()) {
                const { layerViewId, features } = featuresPerLayerView;
                const layerMetaId = this.featureViewModel.getLayerById(layerViewId).getLayerMetaId();
                const featureIds = features.map((f: any) => f.id);
                this.territoryModel.getSelectionModel().addImplicitPointFeatures(layerMetaId, featureIds, false, index !== featuresPerLayerViews.length - 1);
            }
        });
    }

    public unselectImplicitPoints(baseFeatIds: number[]) {
        baseFeatIds = Array.isArray(baseFeatIds) ? baseFeatIds : [baseFeatIds];

        if (baseFeatIds.length === 0) {
            return;
        }

        const pointLayerViews = TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel);

        const datasetPointIdsDfds: JQuery.Promise<any>[] = []
        pointLayerViews.forEach(layerView => {
            datasetPointIdsDfds.push(
                this.getDatasetPointsByLinkAttrs(this.territoryModel.getLinkAttributeName(), baseFeatIds, layerView.id));
        });

        Promise.all(datasetPointIdsDfds).then((featuresPerLayerViews) => {
            for (const [index, featuresPerLayerView] of featuresPerLayerViews.entries()) {
                const { layerViewId, features } = featuresPerLayerView;
                const layerMetaId = this.featureViewModel.getLayerById(layerViewId).getLayerMetaId();
                const featureIds = features.map((f: any) => f.id);
                this.territoryModel.getSelectionModel().removeImplicitPointFeatures(layerMetaId, featureIds, baseFeatIds, index !== featuresPerLayerViews.length - 1);
            }
        });
    }

    /***
     * Get base feature that interacts with the given geometry
     * @param {object} geom GeoJson Geometry to interacts with
     * @return {Promise<Feature[]>} Interacting base features
     */
    private getBaseFeaturesByGeom(geom: Polygon): JQuery.Promise<any[]> {

        if (this.territoryModel.isAccountOnly()) {
            return es.util.deferred.resolved([]);
        }

        const dfd = $.Deferred();
        this.territoryBaseLayer?.featureWorker.anyInteract(geom, (features: any[]) => {
            dfd.resolve(features);
        });
        return dfd.promise();
    }

    public async getDatasetPointIdsByGeom(geom: Polygon, layerViewIds: number[]): Promise<{layerViewId: number, features: Feature[]}[]> {

        const ret: {layerViewId: number, features: Feature[]}[] = [];

        const convertedGeom = ["PN", _.flatten(geom.coordinates[0])];
        const sanitizedGeom = await es.api.geom.sanitize(convertedGeom);

        let spatialCriteria;

        if (JSON.stringify(sanitizedGeom).length > 39000) {
            const geoShape = await es.api.geoShape.store({geom: sanitizedGeom});
            spatialCriteria = {
                $type: "Interacts",
                geoShapeKey: geoShape.key
            } as InteractsCriteria;
        } else {
            spatialCriteria = {
                $type: "Interacts",
                geometry: sanitizedGeom
            } as InteractsCriteria;
        }

        for (let layerViewId of layerViewIds) {
            const layerViewCriteria = this.featureViewModel.getLayerById(layerViewId).getCriteriaClone();
            const criteria = es.util.criteria.and(spatialCriteria, layerViewCriteria);

            const features =
                await es.api.feature.listLean(layerViewId, this.territoryModel.isAccountOnly() ? [] : [this.territoryModel.getLinkAttributeName()], criteria);
            ret.push({layerViewId, features});
        }

        return ret;
    }

    private getAllDatasetPointIdsByGeom(geom: Polygon) {
        const layerIds = TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel).map(lv => lv.id);
        return this.getDatasetPointIdsByGeom(geom, layerIds);
    }

    public async getTerritoriesByGeom(geom: Polygon) {

        const featuresPerLayerViews = await this.getAllDatasetPointIdsByGeom(geom);

        const pointsTerritoryIds = featuresPerLayerViews.flatMap(({ layerViewId, features }) => {

            const layerView = this.featureViewModel.getLayerById(layerViewId);
            const layerMetaId = layerView.getLayerMetaId();
            const levels = this.territoryModel.getTerritoryMetaObjRef().levels;
            let territoryIds: number[] = features.map((feature: any) => {
                const territory = this.territoryModel.getByExceptionFeatureId(layerMetaId, feature.id, levels);
                if (this.territoryModel.isHierarchical() && !this.territoryModel.isLowestLevel() && territory) {
                    return this.territoryModel.get(this.territoryModel.getAncestors(territory.ES_ID, undefined, this.territoryModel.getCurrentLevel())[0])?.ES_ID ?? -1;
                } else {
                    return territory?.ES_ID ?? -1;
                }
            });
            return territoryIds.filter(t => t > 0);
        });


        const baseFeatures = await this.getBaseFeaturesByGeom(geom);
        const featureIds = _.map(baseFeatures, f => f.id);

        const baseFeaturesTerritoryIds: number[] = [];
        const currentLevel = this.territoryModel.isHierarchical() ? this.territoryModel.getCurrentLevel() : null;
        _.each(featureIds, (featureId: number) => {
            const territory = this.territoryModel.getByBaseFeatureId(featureId, currentLevel);
            if (territory && (!territory.ES_LEVEL || territory.ES_LEVEL === currentLevel)) {
                baseFeaturesTerritoryIds.push(territory.ES_ID);
            }
        });

        let filteredIds = _.union(pointsTerritoryIds, baseFeaturesTerritoryIds);

        const selectedList = this.territoryModel.getFilter();
        if (selectedList) { // remove any territory that is already filtered out
            filteredIds = _.intersection(filteredIds, selectedList);
        }

        const childrenIds: number[] = [];
        if (this.territoryModel.isHierarchical()) {
            filteredIds.forEach(id => {
                childrenIds.push(...this.territoryModel.getByParentId(id, true).map(child => child.ES_ID));
            });
            const parentIds = _.uniq(filteredIds.flatMap(id => this.territoryModel.getAncestors(id)));
            filteredIds.push(...childrenIds);
            filteredIds.push(...parentIds);
        }

        return filteredIds;
    }

    private getDatasetPointsByLinkAttrs(linkAttrName: string, linkAttrValues: any[], layerViewId: number): JQuery.Promise<{layerViewId: number, features: any[]}> {

        const datasetPointsDfd = $.Deferred<{layerViewId: number, features: any[]}>();
        const criteria = { $type: "In", attr: linkAttrName, values: linkAttrValues } as InCriteria<any>;
        es.api.feature.listLean(layerViewId, [this.territoryModel.getLinkAttributeName()], criteria).then((features: any) => {
            datasetPointsDfd.resolve({ layerViewId, features });
        });

        return datasetPointsDfd.promise();
    }

    public onTerritoryFeatureMouseOut() {
        this.removeAllOverlays();
        if (this._highlightedPolygonId > 0) {
            this.overlayModule.remove(this._highlightedPolygonId);
            this._highlightedPolygonId = -1;
        }
        if (this._plusSymbolOverlayId > 0) {
            this.overlayModule.remove(this._plusSymbolOverlayId);
            this._plusSymbolOverlayId = -1;
        }
        this._mouseOverFeat = undefined;
    }

    public onTerritoryFeatureMouseOver() {
    }

    public async zoomToTerritoryBBox(territoryIds: number[], padding?: number, disableAnimation?: boolean, showOverlay = true): Promise<void> {
        territoryIds = territoryIds ?? [];

        if (!Array.isArray(territoryIds)) {
            territoryIds = [territoryIds];
        }

        const bboxBaseFeature = async (): Promise<any> => {
            if (this.territoryModel.isAccountOnly()) {
                return Promise.resolve();
            }

            return new Promise(resolve => {
                const baseFeatureIds = this.territoryModel.getBaseFeatureIds(territoryIds);
                this.territoryBaseLayer?.featureWorker.territoryBBox(baseFeatureIds, function(rs: any) {
                    let bbox;
                    if (rs) { // rs can be null if there are no feats assigned to the territory
                        bbox = rs.bbox;
                    }
                    resolve(bbox);
                });
            });
        }

        const bboxBasePoints = async (): Promise<any> => {
            if (this.territoryModel.hasRelatedExtraPointLayer()) {
                return new Promise(resolve => {
                    const pointFeatIds = this.territoryModel.getBasePointFeatureIds(territoryIds);
                    this.extraRelatedPointLayer?.featureWorker.territoryBBox(pointFeatIds, function(rs: any) {
                        let bbox;
                        if (rs) { // rs can be null if there are no feats assigned to the territory
                            bbox = rs.bbox;
                        }
                        resolve(bbox);
                    });
                });
            }
            else {
                return Promise.resolve();
            }
        }

        const bboxPoints = async (): Promise<any> => {
            if (!this.territoryModel.isAccountOnly()) {
                // Points can be ignored on territories with base layer.
                return Promise.resolve();
            }

            const pointLayerViews = TerritoryLayerUtils.getDatasetLayerViews(this.territoryModel, this.featureViewModel);

            const byLayerMetaId: {[lmId: number] : number[]} = {};
            territoryIds.flatMap(t => {
                    const descenders = this.territoryModel.getByParentId(t, true);
                    return descenders.length === 0 ? this.territoryModel.get(t).ES_EXCEPTIONS : descenders.flatMap(d => d.ES_EXCEPTIONS);
                })
                .filter((t) => t != null)
                .forEach((e) => {
                    if (!byLayerMetaId.hasOwnProperty(e.layerMetaId)) {
                        byLayerMetaId[e.layerMetaId] = [];
                    }
                    byLayerMetaId[e.layerMetaId].push(...e.featureIds);
                });

            if (_.isEmpty(byLayerMetaId)) {
                return Promise.resolve();
            }

            const datasetPointIdsDfds: Promise<any>[] = [];

            pointLayerViews
                .filter(lv => byLayerMetaId.hasOwnProperty(lv.layerMetaId))
                .forEach(layerView => {
                    const featureIds = byLayerMetaId[layerView.layerMetaId];
                    const criteria: InCriteria<number> = { $type: "In", attr: "ES_ID", values: featureIds };
                    datasetPointIdsDfds.push(es.api.feature.bounds(layerView.id, criteria));
                });

            const bounds = await Promise.all(datasetPointIdsDfds);
            if (bounds.length === 0) {
                return Promise.resolve();
            }

            const ret = bounds[0];
            for (let i = 1; i < bounds.length; i++) {
                ret[0] = Math.min(bounds[i][0], ret[0])
                ret[1] = Math.min(bounds[i][1], ret[1])
                ret[2] = Math.max(bounds[i][2], ret[2])
                ret[3] = Math.max(bounds[i][3], ret[3])
            }
            return ret;
        }

        if (!territoryIds || territoryIds.length === 0) {
            territoryIds = this.territoryModel.getTerritoryFeaturesObjRef().map(t => t.ES_ID);
        }

        const [baseFeaturesBbox, pointFeaturesBbox, pointsBbox] = await Promise.all([
            bboxBaseFeature(),
            bboxBasePoints(),
            bboxPoints()
        ]);

        let bounds;
        if (baseFeaturesBbox && pointFeaturesBbox) {
            const xMin = Math.min(baseFeaturesBbox[0], pointFeaturesBbox[0]);
            const xMax = Math.max(baseFeaturesBbox[2], pointFeaturesBbox[2]);
            const yMin = Math.min(baseFeaturesBbox[1], pointFeaturesBbox[1]);
            const yMax = Math.max(baseFeaturesBbox[3], pointFeaturesBbox[3]);
            bounds = [xMin, yMin, xMax, yMax]
        } else if (baseFeaturesBbox && !pointFeaturesBbox) {
            bounds = baseFeaturesBbox;
        } else if (!baseFeaturesBbox && pointFeaturesBbox) {
            bounds = pointFeaturesBbox;
        } else if (!baseFeaturesBbox && !pointFeaturesBbox && pointsBbox) {
            bounds = pointsBbox;
        }

        if(!bounds){
            es.log.warn("Bounds could not be calculated for territoryIds", territoryIds);
            return;
        }

        await this.baseMap.setBounds(bounds, padding, disableAnimation);

        // todo: for points, we could show concave hull instead?
        if (showOverlay) {
            // 80% transparent fill so the box is not too dark/severe (default is BLACK at 50%)
            const style = AzureOverlay.getDefaultPolygonStyle(es.util.color.BLACK, 80);
            this.overlayModule.drawBounds(bounds, style, 2500);
        }
    }

    public getConvexHull(territoryIds: number[], isUseCenters = false) {
        console.time("getConvexHull");

        const dfd = $.Deferred();

        const featIds: number[] = [];
        for (const terId of territoryIds) {
            featIds.push(...this.territoryModel.getBaseFeatureIds(terId));
        }

        if (!featIds) {
            return es.util.deferred.rejected();
        }

        this.territoryBaseLayer?.featureWorker.territoryConvexHull(featIds, isUseCenters, function(rs: any) {
            if (!rs || !rs.hull || !rs.hull.geometry) {
                dfd.reject("Could not generate hull");
            } else {
                const flattenedHullCoords = _.flatten(rs.hull.geometry.coordinates[0]);
                //map.overlay.drawBounds(flattenedHullCoords);

                console.timeEnd("getConvexHull");
                dfd.resolve({ hull: ["PN", flattenedHullCoords], bbox: ["PN", rs.bbox] });
            }
        });

        return dfd.promise();
    };

    public processLassoGeomEditEvent(geom: Geometry) {

        if (!geom) {
            return;
        }
        this.setSelectionBusyOverlay();
        if (this.territoryModel.getSelectionModel().getSelectionType() === SELECTION_TYPE.AREAS_AND_ACCOUNTS) {
            this.selectByGeom(es.util.object.deepCopy(geom));
        } else {
            this.selectDatasetPointsByGeom(es.util.object.deepCopy(geom));
        }
    }

    private addListeners() {

        this.internalEventChannel.addListener("onTerritoryFeatureMouseOut", this.onTerritoryFeatureMouseOut, this);
        this.internalEventChannel.addListener("onTerritoryFeatureMouseOver", this.onTerritoryFeatureMouseOver, this);

        this.eventChannel.addListener("onSelectionChange", (_affectedBaseFeatureIds: number[], _affectedPointFeatureIds: number[]) => {
            this.selectedPlusMinusCache = {};
        });

        this.eventChannel.addListener("onTerritoryChange", (_actions: any, _territories: any) => {

        });

        this.eventChannel.addListener("onTerritoryLevelChange", () => {});

        this.eventChannel.addListener("onTerritoryParentChange", function() {});

        this.eventChannel.addListener("onTerritoryFilterChange", function() {});

        this.eventChannel.addListener("onUpdateLayer", function(_lvm: any, _isTransient: boolean, _eventNames: any) {});

        this.eventChannel.addListener("onTerritoryOutlinesLayerUpdate", function() {});

        this.baseMap.map.events.add("mousemove", (e: MapMouseEvent) => this.onMouseMove(e));

        this.baseMap.map.events.add("mouseout", () => this.onTerritoryFeatureMouseOut());

        this.eventChannel.addListener("onOverlayFeatureClick", this.onOverlayFeatureClick, this);

        this.baseMap.map.events.add("zoom", () => {
            if (this._mouseOverFeat != null) {
                const layer = this.baseMap.getLayerById(this._mouseOverFeat.layer.id);
                if (layer && !layer.isVisibleRes()) {
                    this.onTerritoryFeatureMouseOut();
                }
            }
        });
    }



    private onMouseMove(e: MapMouseEvent) {

        if (!this._selectionEnabled || this._isComparisonPage) {
            this.onTerritoryFeatureMouseOut();
            return;
        }

        let shapes: any[] = [];
        if (e.shapes) {
            e.shapes.forEach((shape: any) => {
                const layer = shape.layer ? this.baseMap.getLayerById(shape.layer.id) : undefined;
                if (layer && layer.isVisibleRes() && (
                    (this.territoryModel.isBaseOrRelatedLayer(layer.layerView.id) && this.territoryModel.isLowestLevel()) ||
                    this.territoryModel.isOutlinesLayer(layer.layerView.id)) && TerritoryLayerUtils.isSelectable(layer as ITerritoryLayer, shape)) {
                    shape.layerViewId = layer.layerView.id;
                    shapes.push(shape);
                }
            });
        }
        //Prevent duplicates when the same feature is on multiple layers (eg. Polygon + Stroke)
        shapes = _.uniqWith(shapes, (a, b) => a.id === b.id && a.layerViewId == b.layerViewId);

        if (shapes.length === 0) {
            this.onTerritoryFeatureMouseOut();
            return;
        }

        // select the shape to highlight
        const shape = shapes.sort((a, b) => {
            return b.layer.minzoom - a.layer.minzoom;
        })[0];
            /*
            .filter(feat => {
                // TODO: intentional?
                return true;
                //const layer = feat?.layer ? this.baseMap.getLayerById(feat.layer.id) : undefined;
                //return layer && (this.territoryModel.isOutlinesLayer(layer.layerView.id) || this.territoryModel.isBaseOrRelatedLayer(layer.layerView.id));
            })
            */


        if(!shape || shape.id == this._mouseOverFeat?.id) {
            return;
        }

        const territoryLayer = this.baseMap.getLayerById(shape.layer.id) as ITerritoryBaseLayer;
        if(!territoryLayer){
            console.error("No layer found for layerViewId: "+ shape.layer.layerViewId);
            return;
        }

        territoryLayer.featureWorker.getFeatureById(shape.id).then((fullFeat: geojson.Feature) => {
            if(!fullFeat){
                console.error("There should be a feature for the shape", shape);
                return;
            }

            //const ter = territoryLayer.isBaseLayer() ? this.territoryModel.getByBaseFeatureId(this._mouseOverFeat?.id) : undefined;
            this._mouseOverFeat = shape;
            const geom = fullFeat.geometry;
            const style = {
                $type: "PolygonStyle",
                line: {
                    color: es.util.color.BLACK,  // WHITE, OLD_LABEL_FILL_BLUE, HILITE_COLOR_TURQUOISE, ter?.ES_STYLE_COLOR, territoryLayer.layerView.styleSettings.defaultStyle.line.color
                    width: 3
                },
                fillColor: 0
            };

            //es.util.color._debug(style.line.color, "to");
            
            if (this._highlightedPolygonId > -1) {
                if(this.overlayModule.hasOverlayId(this._highlightedPolygonId)){
                    this.overlayModule.update(this._highlightedPolygonId, geom, undefined, style);
                } else {
                    // think user has mousedover a LLL. treat it like a normal MouseOut
                    this.onTerritoryFeatureMouseOut();
                    this._highlightedPolygonId = this.overlayModule.add(geom, style);
                }
            } else {
                this._highlightedPolygonId = this.overlayModule.add(geom, style);
            }

            const point = new data.Point([0, 0]);
            if (shape.properties.vc) {
                point.coordinates = JSON.parse(shape.properties.vc);
            } else {
                // FIXME :: This should not calculate the visual center on mouse move. Get the visual center from the outlines layer worker
                console.warn(`Feature has no visual center (${territoryLayer.layerView}_${shape.id}!`);
            }
            let label = territoryLayer.createLabel(shape);
            if (territoryLayer instanceof  TerritoryBaseLayer) {

                const terr = this.territoryModel.getTerritoryByBaseFeatureId(+shape.id);
                if (terr) {
                    label = label + " (" + terr.NAME + ")";
                }
            }
            // todo: ignore llls
            if (this._plusSymbolOverlayId > 0) {
                this.overlayModule.update(this._plusSymbolOverlayId, point, label);
                this.updatePlusMinusSymbol(territoryLayer, shape.id);
            } else {
                const style = this.PLUS_SYMBOL_STYLE;
                if (this._isComparisonPage) {
                    style.size = 0;
                }
                this._plusSymbolOverlayId = this.overlayModule.add(point, style, label, {suppressEvents: true});
            }
        });
    }

    private onOverlayFeatureClick(evt: OverlayFeatureClickEvent) {
        const shape = evt.shape;
        const isClickOnTerritoryPlusMinus = shape.layer?.id === AzureOverlay.SYMBOL_LAYER_ID && shape.id === this._plusSymbolOverlayId;
        //es.log.log("onOverlayFeatureClick", "isClickOnTerritoryPlusMinus", isClickOnTerritoryPlusMinus, shape);

        if (isClickOnTerritoryPlusMinus) {
            if (this._isComparisonPage || this.lassoFillHandler?.isLassoActive()) {
                return;
            }
            const layer = this.baseMap.getLayerById(this._mouseOverFeat.layer.id);
            if (layer) {
                this.selectByLayerViewAndFeatureId(layer.layerView.id, this._mouseOverFeat.id);
            }
        }
    }

    private updatePlusMinusSymbol(layer: ITerritoryBaseLayer, featureId: number) {
        // return;
        if (this._plusSymbolOverlayId < 0) {
            return;
        }

        const style = this.isFeatSelected(layer, featureId) ?
            this.MINUS_SYMBOL_STYLE : this.PLUS_SYMBOL_STYLE;

        this.overlayModule.update(this._plusSymbolOverlayId, undefined, undefined, style);
    }
}


